import React, { useState, useEffect } from 'react'
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
  Link,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useCreateEnquiryMutation } from './createEnquiry.generated'

export interface EnquiryFormData {
  name: string
  email: string
  phone: string
  message: string
}

interface InlineEnquiryFormProps {
  venueName: string
  matterportId: string
  stitchedTourUUID?: string
  onBack: () => void
  onSubmit: () => void
  resetTrigger?: boolean
  buttonColor?: string
}

const InlineEnquiryForm: React.FC<InlineEnquiryFormProps> = ({
  venueName,
  matterportId,
  stitchedTourUUID,
  onBack,
  onSubmit,
  resetTrigger,
  buttonColor,
}) => {
  const [formData, setFormData] = useState<EnquiryFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  })

  const [errors, setErrors] = useState<Partial<EnquiryFormData>>({})
  const [consentChecked, setConsentChecked] = useState(false)
  const [consentError, setConsentError] = useState('')
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [, createEnquiry] = useCreateEnquiryMutation()

  // Reset form when component is shown again
  useEffect(() => {
    const resetFormData = {
      name: '',
      email: '',
      phone: '',
      message: '',
    }
    setFormData(resetFormData)
    setErrors({})
    setConsentChecked(false)
    setConsentError('')
  }, [resetTrigger])

  const handleChange =
    (field: keyof EnquiryFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }))

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }))
      }
    }

  const validateForm = (): boolean => {
    const newErrors: Partial<EnquiryFormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    }

    setErrors(newErrors)

    if (!consentChecked) {
      setConsentError('You must consent to sharing your details')
      return false
    }

    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const result = await createEnquiry({
        input: {
          name: formData.name,
          email: formData.email,
          telephone: formData.phone,
          message: formData.message,
          ...(stitchedTourUUID ? { stitchedTourUUID } : { matterportID: matterportId }),
        },
      })

      if (result.error) {
        setShowErrorToast(true)
        setIsSubmitting(false)
        return
      }

      setShowSuccessToast(true)
      // Wait for toast to be visible before closing
      setTimeout(() => {
        setIsSubmitting(false)
        onSubmit()
      }, 500)
    } catch (error) {
      setShowErrorToast(true)
      setIsSubmitting(false)
    }
  }

  const handleCloseSuccessToast = () => {
    setShowSuccessToast(false)
  }

  const handleCloseErrorToast = () => {
    setShowErrorToast(false)
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
        }}>
        <IconButton onClick={onBack} size='small' sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h6' color='text.primary' component='h3' sx={{ fontWeight: 600 }}>
          Make an enquiry
        </Typography>
      </Box>

      <Box component='form' onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label='Your name'
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          margin='normal'
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label='Your email'
          type='email'
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          margin='normal'
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label='Your phone'
          type='tel'
          value={formData.phone}
          onChange={handleChange('phone')}
          error={!!errors.phone}
          helperText={errors.phone}
          margin='normal'
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={`Message to ${venueName}`}
          multiline
          rows={4}
          value={formData.message}
          onChange={handleChange('message')}
          error={!!errors.message}
          helperText={errors.message}
          margin='normal'
          required
          sx={{ mb: 3 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={consentChecked}
              onChange={(e) => {
                setConsentChecked(e.target.checked)
                if (e.target.checked) {
                  setConsentError('')
                }
              }}
            />
          }
          label={
            <Typography variant='body2' color={consentError ? 'error' : 'text.secondary'}>
              I consent to my contact details and message being shared with {venueName} so they can
              respond to my enquiry. I understand my data will be processed in line with
              HeadBox&apos;s{' '}
              <Link
                href='https://www.headbox.com/policies/privacy'
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}>
                Privacy Policy
              </Link>
              .
            </Typography>
          }
          sx={{ mb: 3, alignItems: 'flex-start', ml: 0 }}
        />
        {consentError && (
          <Typography variant='caption' color='error' sx={{ display: 'block', mt: -2, mb: 2 }}>
            {consentError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onBack} color='inherit'>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color='inherit' /> : undefined}
            sx={{
              bgcolor: buttonColor || '#1976d2',
              '&:hover': {
                bgcolor: buttonColor || '#1976d2',
                opacity: 0.9,
              },
            }}>
            {isSubmitting ? 'Sending...' : 'Send enquiry'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showSuccessToast}
        autoHideDuration={6000}
        onClose={handleCloseSuccessToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSuccessToast} severity='success' sx={{ width: '100%' }}>
          Your enquiry has been sent successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseErrorToast} severity='error' sx={{ width: '100%' }}>
          Failed to send enquiry. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default InlineEnquiryForm
